import { Chip, Rating, styled, Tooltip } from '@mui/material';
import { Circle, CircleFilled, Warning } from '@tempo-io/sui-icons';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getCustomFieldDateDisplay, removeSystemFieldNamePrefix } from '../../utils/Utilities';
import { FlexDiv } from './CustomFieldInput';
import * as Styled from './Styled';

// Root `resolutions` pins i18next to a single hoisted install across the workspace, so the
// branded TFunction is safe here; re-add a structural shim if that guarantee ever changes.
export type TranslationFunction = TFunction;

export const getCurrencySymbol = (currency: string | undefined | null) => {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    default:
      return '';
  }
};

const StyledRating = styled(Rating)(({ theme }) => ({
  opacity: '1 !important',
  '& .MuiRating-iconFilled': {
    color: theme.palette.primary.main,
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.primary.light,
  },
}));


type CustomFieldValueProps = {
  fieldType?: string;
  values?: string[];
  textOnly?: string;
  minimize?: boolean;
};

export const getFormulaDataString = (t: TFunction, values: string[]) => {
  values = values.map((value) => removeSystemFieldNamePrefix(value));
  let value = values[0];
  let hasError = false;
  if (values && values.length > 2) {
    hasError = true;
    const moreCount = values.length - 3;
    let errorText: string = t(($) => $.ideaManagement.general.missingField, {
      fieldName: values[2],
    });
    if (moreCount >= 2) {
      errorText = t(($) => $.ideaManagement.general.missingFieldAndNMore, {
        fieldName: values[2],
        count: moreCount,
      });
    } else if (moreCount === 1) {
      errorText = t(($) => $.ideaManagement.general.missingFieldAnd, {
        fieldName: values[2],
        fieldName2: values[3],
      });
    }

    value = t(($) => $.ideaManagement.general.formulaValueCannotBeCalculated, {
      error: errorText,
    });
  } else if (values && values.length > 1) {
    hasError = true;
    value = t(($) => $.ideaManagement.general.formulaValueCannotBeCalculated, {
      error: `Error: ${values[1]}`,
    });
  }
  return { value, hasError };
};

const getFormulaDataStringValueOnly = (t: TFunction, values: string[]) => {
  const { value } = getFormulaDataString(t, values ?? []);
  return value;
};

export const getCustomFieldValueAsString = (
  t: TFunction,
  fieldType: string,
  values?: string[],
  currency?: string | null | undefined,
) => {
  switch (fieldType) {
    case 'RATING':
      return (
        <StyledRating
          data-testid={'fim3-custom-field-input-rating-read-only'}
          value={values ? parseInt(values[0] ?? '0') : 0}
          precision={1}
          icon={<CircleFilled className="size-6" />}
          emptyIcon={<Circle className="size-6" />}
          disabled
        />
      );
    case 'FORMULA':
      return getFormulaDataStringValueOnly(t, values ?? []);
    case 'CURRENCY':
      return values?.[0] ? getCurrencySymbol(currency) + values[0] : '';
    case 'DATE':
    case 'DATE_RANGE':
      return values ? values.map((el) => getCustomFieldDateDisplay(el)).join(' - ') : '';
    case 'FIBONACCI':
      return values?.[0] ?? '';
    default:
      return values?.filter((el) => !!el).join(', ');
  }
};

export const CustomFieldValue = ({
  fieldType,
  textOnly,
  values,
  minimize,
}: CustomFieldValueProps) => {
  const { t } = useTranslation();

  if (fieldType === undefined) return;

  const value = values ? (values[0] ?? '') : '';

  if (value === '' && fieldType !== 'FORMULA') return;

  const { value: formulaText, hasError } = getFormulaDataString(t, values ?? []);

  if (textOnly && fieldType !== 'FORMULA') {
    return <p className="text-sm leading-normal">{values?.toString()}</p>;
  }

  switch (fieldType) {
    case 'LIST':
      return (
        <FlexDiv>
          {values?.map((el) => (
            <Chip
              className="fs-exclude"
              data-dd-action-name="FIM 3 Custom Field - value redracted"
              key={el}
              label={el}
              size="small"
            />
          ))}
        </FlexDiv>
      );
    case 'PERCENT':
      return (
        <Styled.PercentContainer>
          <Styled.PercentBar
            className="fs-exclude"
            data-dd-action-name="FIM 3 Custom Field - value redacted"
            percent={value ? parseInt(value) : 0}
          >
            {value ? `${value}%` : null}
          </Styled.PercentBar>
        </Styled.PercentContainer>
      );
    case 'RATING':
      return (
        <Styled.FieldRating
          className="fs-exclude"
          data-dd-action-name="FIM 3 Custom Field - value redacted"
          value={parseInt(value)}
          precision={1}
          readOnly
          icon={<CircleFilled className="size-6" />}
          emptyIcon={<Circle className="size-6" />}
        />
      );
    case 'DATE':
    case 'DATE_RANGE':
      return (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - value redacted"
        >
          {values ? values.map((el) => getCustomFieldDateDisplay(el)).join(' - ') : ''}
        </p>
      );
    case 'FIBONACCI':
      return (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - fibonacci value redacted"
        >
          {value}
        </p>
      );
    case 'FORMULA':
      if (minimize && hasError) {
        return (
          <Tooltip
            className="fs-exclude"
            data-dd-action-name="FIM 3 Custom Field - formula redacted"
            title={formulaText}
          >
            <Warning className="text-icon-warning" />
          </Tooltip>
        );
      }

      return hasError ? (
        <FlexDiv>
          <Warning className="text-icon-warning" />
          <p
            className="fs-exclude text-sm leading-normal"
            data-dd-action-name="FIM 3 Custom Field - formula redacted"
          >
            {formulaText}
          </p>
        </FlexDiv>
      ) : (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - formula redacted"
        >
          {formulaText}
        </p>
      );
    default:
      return (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - formula redacted"
        >
          {value}
        </p>
      );
  }
};
